import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  Tag,
  Avatar,
  Typography,
  message,
  Popconfirm,
  Select,
  Row,
  Col,
  Image,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  StarOutlined,
  StarFilled,
  CheckCircleOutlined,
  StopOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import petsService, { Pet } from '../services/petsService';
import { getImageUrl } from '../config/api';

const { Title, Text } = Typography;
const { Option } = Select;

const firstImageUrl = (pet: Pet): string => {
  const img = pet.images && pet.images.length > 0 ? pet.images[0] : undefined;
  if (!img) return '';
  if (typeof img === 'string') return getImageUrl(img);
  return getImageUrl(img.url || img.image || '');
};

const nameOf = (ref?: { name?: string; title?: string } | string | null): string => {
  if (!ref) return '-';
  if (typeof ref === 'string') return ref;
  return ref.name || ref.title || '-';
};

const Pets: React.FC = () => {
  const navigate = useNavigate();
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [availability, setAvailability] = useState<'all' | 'available' | 'unavailable'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchPets = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: pageSize };
      if (search.trim()) params.search = search.trim();
      if (availability !== 'all') params.isAvailable = availability === 'available';
      const { pets: list, pagination } = await petsService.getPets(params);
      setPets(list);
      setTotal(pagination.totalItems ?? pagination.total ?? (pagination.totalPages ? pagination.totalPages * pageSize : list.length));
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to fetch pets');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, availability]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  const handleDelete = async (id: string) => {
    try {
      await petsService.deletePet(id);
      message.success('Pet listing deleted');
      fetchPets();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete pet');
    }
  };

  const toggleFlag = async (
    pet: Pet,
    flag: 'isAvailable' | 'isFeatured',
    next: boolean,
    label: string,
  ) => {
    try {
      await petsService.setPetFlag(pet._id, flag, next);
      message.success(`${label} ${next ? 'enabled' : 'disabled'}`);
      fetchPets();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || `Failed to update ${label}`);
    }
  };

  const columns = [
    {
      title: 'Pet',
      key: 'pet',
      render: (record: Pet) => {
        const url = firstImageUrl(record);
        return (
          <Space>
            {url ? (
              <Image
                src={url}
                alt={record.title}
                width={54}
                height={54}
                style={{ objectFit: 'cover', borderRadius: 8 }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='54' height='54'%3E%3Crect width='54' height='54' fill='%23eee'/%3E%3C/svg%3E"
              />
            ) : (
              <div style={{ width: 54, height: 54, borderRadius: 8, background: '#f0f0f0' }} />
            )}
            <div>
              <div style={{ fontWeight: 600 }}>{record.title}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {nameOf(record.petCategory as any)}
                {record.breed ? ` · ${nameOf(record.breed as any)}` : ''}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Price',
      key: 'price',
      render: (record: Pet) => (
        <Space direction="vertical" size={0}>
          <Text strong>₹{record.price?.toLocaleString?.() ?? record.price}</Text>
          {record.isNegotiable ? <Tag color="blue">Negotiable</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Owner',
      key: 'owner',
      render: (record: Pet) => {
        const owner: any = record.owner;
        return (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <div>
              <div>{nameOf(owner)}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {typeof owner === 'object' ? owner?.phone || owner?.email || '' : ''}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'address',
      key: 'address',
      render: (address?: string) => <Text style={{ fontSize: 12 }}>{address || '-'}</Text>,
      ellipsis: true,
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: Pet) => (
        <Space wrap size={4}>
          {record.isAvailable ? (
            <Tag color="green">Available</Tag>
          ) : (
            <Tag color="red">Sold / Hidden</Tag>
          )}
          {record.isApproved ? <Tag color="cyan">Approved</Tag> : <Tag color="orange">Pending</Tag>}
          {record.isFeatured ? <Tag color="gold">Featured</Tag> : null}
          {record.isPremium ? <Tag color="purple">Premium</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Listed',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: string) => (d ? new Date(d).toLocaleDateString() : '-'),
      width: 110,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (record: Pet) => (
        <Space>
          <Tooltip title="View details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/pets/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title={record.isAvailable ? 'Hide (mark unavailable)' : 'Make available'}>
            <Button
              size="small"
              icon={record.isAvailable ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={() =>
                toggleFlag(record, 'isAvailable', !record.isAvailable, 'Availability')
              }
            />
          </Tooltip>
          <Tooltip title={record.isFeatured ? 'Unfeature' : 'Feature this listing'}>
            <Button
              size="small"
              icon={record.isFeatured ? <StarFilled style={{ color: '#f6b100' }} /> : <StarOutlined />}
              onClick={() => toggleFlag(record, 'isFeatured', !record.isFeatured, 'Featured')}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this pet listing?"
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Pet Listings
          </Title>
          <Text type="secondary">Manage marketplace pet ads — feature, hide, or remove listings.</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchPets}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search by title / breed / location"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              onPressEnter={fetchPets}
            />
          </Col>
          <Col>
            <Select
              value={availability}
              style={{ width: 180 }}
              onChange={(v) => {
                setAvailability(v);
                setPage(1);
              }}
            >
              <Option value="all">All listings</Option>
              <Option value="available">Available only</Option>
              <Option value="unavailable">Sold / Hidden</Option>
            </Select>
          </Col>
        </Row>

        <Table
          rowKey="_id"
          loading={loading}
          columns={columns as any}
          dataSource={pets}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} listings`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default Pets;
